import {
  addHours,
  addDays,
  parse,
  startOfTomorrow,
  startOfDay
} from 'date-fns';

const DAYS = 'days';
const HOURS = 'hours';
export const getNowAtLocation = () => new Date();

/**
 * Returns the minimum datetime value allowed for adding
 * an item to the cart.
 *
 * Depends current time at the location, menu lead time,
 * menu cutoff time, and the lead time on the product itself.
 *
 * If the minimum datetime value for the item is after the
 * current fulfillmentDateTime value on the cart, clicking
 * on the item card will result in a redirect to the order settings modal,
 * at which point the user is prompted to select a new fulfillment
 * date + time in order to satisfy the lead time requirements of this item.
 */
export const getMinimumFulfillmentDateTimeForItem = (
  menuItem,
  menuLeadTime
) => {
  const cutoffTime = menuLeadTime.cutoff_time;
  const now = getNowAtLocation();

  const cutoffDateTime = cutoffTime ? parse(cutoffTime, 'HH:mm:ss', now) : null;
  const isCutoffTimeActive = cutoffDateTime && cutoffDateTime < now;
  const startTime = isCutoffTimeActive ? startOfTomorrow() : now;

  const typeOfLeadTime = getTypeOfLeadTimeToApply(menuItem, menuLeadTime);
  const additionalLeadTimeToApply = getAdditionalLeadTimeToApply(
    menuItem,
    menuLeadTime
  );

  if (additionalLeadTimeToApply) {
    if (typeOfLeadTime === DAYS) {
      // when adding additional lead time for days we do a 'calendar date' addition, which means we need to return the start of the day (midnight) after adding the additional days.
      return startOfDay(addDays(startTime, additionalLeadTimeToApply));
    }
    return addHours(startTime, additionalLeadTimeToApply);
  }

  // this is startTime instead of 'now' b/c we need to account for cutoff time even if there is no additional lead time
  return startTime;
};

/**
 * This function returns text to display on the menu item card
 * when the item has a longer lead time than the menu.
 *
 * While there is some logic here that is shared with
 * the getMinimumFulfillmentDateTimeForItem function, this
 * function has a completely different purpose, and is
 * not dependent on the cutoff time set on the menu.
 */
export const getItemLeadTimeText = (menuItem, menuLeadTime) => {
  const shouldDisplayLeadTime = isItemLeadTimeGreaterThanMenuLeadTime(
    menuItem,
    menuLeadTime
  );
  if (!shouldDisplayLeadTime) {
    return null;
  }

  const {
    lead_time_days: menuLeadTimeDays,
    lead_time_hours: menuLeadTimeHours
  } = menuLeadTime;

  const {
    lead_time_days: itemLeadTimeDays,
    lead_time_hours: itemLeadTimeHours
  } = menuItem.product;

  if (getTypeOfLeadTimeToApply(menuItem, menuLeadTime) === DAYS) {
    return `This item has an extra ${itemLeadTimeDays -
      menuLeadTimeDays} day lead time.`;
  }

  return `This item has an extra ${itemLeadTimeHours -
    menuLeadTimeHours} hour lead time.`;
};

export const getTypeOfLeadTimeToApply = (menuItem, menuLeadTime) => {
  if (menuLeadTime.lead_time_days || menuItem.product.lead_time_days) {
    return DAYS;
  }
  return HOURS;
};

export const getAdditionalLeadTimeToApply = (menuItem, menuLeadTime) => {
  const itemLeadTimeGreaterThanMenu = isItemLeadTimeGreaterThanMenuLeadTime(
    menuItem,
    menuLeadTime
  );

  if (itemLeadTimeGreaterThanMenu) {
    const { product } = menuItem;
    return product.lead_time_days || product.lead_time_hours;
  }
  return 0;
};

export const isItemLeadTimeGreaterThanMenuLeadTime = (
  menuItem,
  menuLeadTime
) => {
  const {
    lead_time_days: menuLeadTimeDays,
    lead_time_hours: menuLeadTimeHours
  } = menuLeadTime;

  const {
    lead_time_days: itemLeadTimeDays,
    lead_time_hours: itemLeadTimeHours
  } = menuItem.product;

  if (menuLeadTimeDays) {
    if (itemLeadTimeDays) {
      return itemLeadTimeDays > menuLeadTimeDays;
    } else {
      // days takes precedence over hours
      return false;
    }
  } else {
    if (itemLeadTimeDays) {
      // days takes precedence over hours
      return true;
    } else {
      return itemLeadTimeHours > menuLeadTimeHours;
    }
  }
};
